import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  ArrowLeft, Mail, Phone, Calendar, DollarSign, Tag,
  Clock, MapPin, Edit2, Star, Trophy, TrendingUp,
  Zap, ShoppingBag, UserCheck, MessageCircle, CreditCard, Plus, Trash2, Check, X,
  Award, Coins, Gift, Crown, ChevronRight, Palette, Save, Pipette, History, ChevronDown,
  MoreVertical, Ban, Archive, ArchiveRestore, AlertTriangle, ShieldOff
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ClientActivityLog from "../components/ClientActivityLog";
import FormulaPanel from "../components/FormulaPanel";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

const stageBadge = {
  active: { bg: "bg-[#E8F0E9]", text: "text-emerald-600" },
  vip: { bg: "bg-rose-50", text: "text-slate-900", icon: Star },
  lead: { bg: "bg-[#EAF0F6]", text: "text-blue-600" },
  "at-risk": { bg: "bg-[#FDF3E1]", text: "text-amber-600" },
  churned: { bg: "bg-rose-50", text: "text-rose-600" },
  banned: { bg: "bg-rose-100", text: "text-rose-700", icon: Ban },
};

export default function ClientProfilePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [score, setScore] = useState(null);
  const [cards, setCards] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardForm, setCardForm] = useState({ card_number: "", exp_month: "", exp_year: "", cardholder_name: "" });
  const [cardSaving, setCardSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formulas, setFormulas] = useState([]);
  const [showAddFormula, setShowAddFormula] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaForm, setFormulaForm] = useState({ title: "", formula: "", brand: "", developer: "", processing_time: "", notes: "" });
  const [formulaSaving, setFormulaSaving] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);

  // Client moderation (ban / deactivate / delete)
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banSaving, setBanSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePin, setDeletePin] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  // Bumps to force ClientActivityLog to re-fetch after a moderation action
  const [activityRefresh, setActivityRefresh] = useState(0);

  const loadCards = async () => {
    try { const res = await api.get(`/clients/${clientId}/cards`); setCards(res.data); } catch {}
  };

  const loadFormulas = async () => {
    try { const res = await api.get(`/clients/${clientId}/color-formulas`); setFormulas(res.data); } catch {}
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [clientRes, scoreRes] = await Promise.all([
          api.get(`/clients/${clientId}`),
          api.get(`/client-scores/${clientId}`).catch(() => ({ data: null })),
        ]);
        setClient(clientRes.data);
        setScore(scoreRes.data);
        try { const cardsRes = await api.get(`/clients/${clientId}/cards`); setCards(cardsRes.data); } catch {}
        try { const fRes = await api.get(`/clients/${clientId}/color-formulas`); setFormulas(fRes.data); } catch {}
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId]);

  const addCard = async () => {
    if (!cardForm.card_number || cardForm.card_number.length < 13) { toast.error("Enter a valid card number"); return; }
    setCardSaving(true);
    try {
      await api.post(`/clients/${clientId}/cards`, cardForm);
      toast.success("Card saved on file");
      setShowAddCard(false);
      setCardForm({ card_number: "", exp_month: "", exp_year: "", cardholder_name: "" });
      loadCards();
    } catch { toast.error("Failed to save card"); }
    finally { setCardSaving(false); }
  };

  const deleteCard = async (cardId) => {
    try { await api.delete(`/clients/${clientId}/cards/${cardId}`); toast.success("Card removed"); loadCards(); } catch { toast.error("Failed to remove"); }
  };

  const setDefaultCard = async (cardId) => {
    try { const res = await api.put(`/clients/${clientId}/cards/${cardId}/default`); setCards(res.data); toast.success("Default card updated"); } catch { toast.error("Failed"); }
  };

  // ───── Moderation handlers ─────
  const submitBan = async () => {
    setBanSaving(true);
    try {
      await api.post(`/clients/${clientId}/ban`, { reason: banReason });
      toast.success("Client banned");
      setBanDialogOpen(false);
      setBanReason("");
      // Refetch profile so badge + banner update
      const res = await api.get(`/clients/${clientId}`);
      setClient(res.data);
      setActivityRefresh(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to ban client");
    } finally { setBanSaving(false); }
  };

  const unbanClient = async () => {
    try {
      await api.post(`/clients/${clientId}/unban`);
      toast.success("Client unbanned");
      const res = await api.get(`/clients/${clientId}`);
      setClient(res.data);
      setActivityRefresh(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unban");
    }
  };

  const deactivateClient = async () => {
    setDeactivating(true);
    try {
      await api.post(`/clients/${clientId}/deactivate`);
      toast.success("Client archived");
      const res = await api.get(`/clients/${clientId}`);
      setClient(res.data);
      setActivityRefresh(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive");
    } finally { setDeactivating(false); }
  };

  const reactivateClient = async () => {
    try {
      await api.post(`/clients/${clientId}/reactivate`);
      toast.success("Client restored");
      const res = await api.get(`/clients/${clientId}`);
      setClient(res.data);
      setActivityRefresh(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    }
  };

  const submitDelete = async () => {
    if (!deletePin || deletePin.length < 4) {
      setDeleteError("Enter your manager PIN");
      return;
    }
    setDeleteError("");
    setDeleting(true);
    try {
      await api.delete(`/clients/${clientId}`, { headers: { "X-Manager-Pin": deletePin } });
      toast.success("Client deleted");
      setDeleteDialogOpen(false);
      navigate("/clients");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to delete";
      setDeleteError(msg);
    } finally { setDeleting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-400">Client not found</p>
        <Button onClick={() => navigate("/clients")} variant="outline" className="mt-4">Go back</Button>
      </div>
    );
  }

  const badge = stageBadge[client.pipeline_stage] || stageBadge.active;
  const appointments = client.appointments || [];
  const transactions = client.transactions || [];

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="client-profile-page">
      {/* Back */}
      <button
        data-testid="back-to-clients"
        onClick={() => navigate("/clients")}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Clients
      </button>

      {/* Profile Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8 mb-6 animate-fadeIn">
        {/* Banned banner */}
        {client.banned && (
          <div data-testid="banned-banner" className="mb-5 flex items-start gap-3 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200">
            <Ban className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-rose-700">This client is banned</p>
              {client.banned_reason && (
                <p className="text-xs text-rose-600/80 mt-0.5">Reason: {client.banned_reason}</p>
              )}
              <p className="text-[11px] text-rose-500/70 mt-1">Online bookings are blocked until you unban.</p>
            </div>
            <Button data-testid="unban-btn" size="sm" variant="outline" onClick={unbanClient} className="border-rose-300 text-rose-700 hover:bg-rose-100 h-8 text-xs">
              Unban
            </Button>
          </div>
        )}
        {/* Archived banner */}
        {client.is_active === false && !client.banned && (
          <div data-testid="archived-banner" className="mb-5 flex items-start gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
            <Archive className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-700">This client is archived</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Hidden from the active client list. Online bookings are blocked.</p>
            </div>
            <Button data-testid="reactivate-btn" size="sm" variant="outline" onClick={reactivateClient} className="border-slate-300 text-slate-700 h-8 text-xs">
              <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Restore
            </Button>
          </div>
        )}
        <div className="flex items-start gap-6">
          <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center text-xl font-medium text-slate-900 flex-shrink-0">
            {client.first_name?.[0]}{client.last_name?.[0]}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-['Outfit'] text-2xl font-medium tracking-tight text-slate-900">
                {client.first_name} {client.last_name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                {badge.icon && <badge.icon className="w-3 h-3" />}
                {client.pipeline_stage}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
              {client.email && (
                <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" /> {client.email}</span>
              )}
              {client.phone && (
                <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" /> {client.phone}</span>
              )}
              {client.birthday && (
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {client.birthday}</span>
              )}
            </div>
            <div className="flex gap-1.5 mt-3">
              {(client.tags || []).map(tag => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-[#FAFAFA] text-slate-600 border border-slate-200">{tag}</span>
              ))}
            </div>
          </div>
          {/* Moderation actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="client-actions-trigger"
                className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                aria-label="Client actions"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {!client.banned ? (
                <DropdownMenuItem
                  data-testid="action-ban"
                  onSelect={() => { setBanReason(""); setBanDialogOpen(true); }}
                  className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 cursor-pointer"
                >
                  <Ban className="w-4 h-4 mr-2" /> Ban client…
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  data-testid="action-unban"
                  onSelect={unbanClient}
                  className="text-emerald-600 focus:text-emerald-700 focus:bg-emerald-50 cursor-pointer"
                >
                  <ShieldOff className="w-4 h-4 mr-2" /> Unban client
                </DropdownMenuItem>
              )}
              {client.is_active === false ? (
                <DropdownMenuItem
                  data-testid="action-restore"
                  onSelect={reactivateClient}
                  className="cursor-pointer"
                >
                  <ArchiveRestore className="w-4 h-4 mr-2" /> Restore from archive
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  data-testid="action-deactivate"
                  onSelect={deactivateClient}
                  disabled={deactivating}
                  className="cursor-pointer"
                >
                  <Archive className="w-4 h-4 mr-2" /> Archive client
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="action-delete"
                onSelect={() => { setDeletePin(""); setDeleteError(""); setDeleteDialogOpen(true); }}
                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 cursor-pointer"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete permanently…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Lifetime Value", value: `$${(client.lifetime_value || 0).toFixed(0)}`, icon: DollarSign },
          { label: "Total Visits", value: client.visit_count || 0, icon: Calendar },
          { label: "Avg Ticket", value: `$${(client.average_ticket || 0).toFixed(0)}`, icon: Tag },
          { label: "Last Visit", value: client.last_visit ? new Date(client.last_visit).toLocaleDateString() : "N/A", icon: Clock },
        ].map((stat) => (
          <div key={stat.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{stat.label}</span>
            </div>
            <p className="font-['Outfit'] text-xl font-medium text-slate-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Loyalty Card */}
      {client.loyalty ? (
        <div data-testid="loyalty-card" className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{
                backgroundColor: client.loyalty.tier === "Platinum" ? "#1A1A1A" :
                  client.loyalty.tier === "Gold" ? "#FEF3CD" :
                  client.loyalty.tier === "Silver" ? "#F0EFED" : "#F5E6D3"
              }}>
                <Crown className="w-5 h-5" style={{
                  color: client.loyalty.tier === "Platinum" ? "#fff" :
                    client.loyalty.tier === "Gold" ? "#F59E0B" :
                    client.loyalty.tier === "Silver" ? "#6B7280" : "#CD7F32"
                }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-['Outfit'] font-semibold text-slate-900">Loyalty Member</h3>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{
                    backgroundColor: client.loyalty.tier === "Platinum" ? "#1A1A1A" :
                      client.loyalty.tier === "Gold" ? "#F59E0B" :
                      client.loyalty.tier === "Silver" ? "#C0C0C0" : "#CD7F32",
                    color: client.loyalty.tier === "Platinum" || client.loyalty.tier === "Gold" ? "#fff" : "#fff"
                  }}>
                    {client.loyalty.tier}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Member since {client.loyalty.enrolled_at ? new Date(client.loyalty.enrolled_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
              <Coins className="w-4 h-4 text-[#F59E0B] mx-auto mb-1" />
              <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{(client.loyalty.points_balance || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-400">Points Balance</p>
            </div>
            <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
              <DollarSign className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
              <p className="font-['Outfit'] text-lg font-semibold text-emerald-600">${((client.loyalty.points_balance || 0) * (client.loyalty.dollar_value || 0.01)).toFixed(2)}</p>
              <p className="text-[10px] text-slate-400">Point Value</p>
            </div>
            <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
              <TrendingUp className="w-4 h-4 text-[#8B5CF6] mx-auto mb-1" />
              <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{(client.loyalty.total_earned || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-400">Total Earned</p>
            </div>
            <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
              <Gift className="w-4 h-4 text-[#EC4899] mx-auto mb-1" />
              <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{(client.loyalty.total_redeemed || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-400">Redeemed</p>
            </div>
          </div>

          {/* Next tier progress */}
          {client.loyalty.next_tier && (
            <div className="bg-[#FEF3CD] border border-[#FCEABB] rounded-lg p-3 flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-[#856404] flex-shrink-0" />
              <p className="text-xs text-[#856404]">
                <strong>{(client.loyalty.next_tier.min_points - (client.loyalty.total_earned || 0)).toLocaleString()}</strong> more points to reach <strong>{client.loyalty.next_tier.name}</strong> ({client.loyalty.next_tier.discount_pct}% discount)
              </p>
            </div>
          )}
          {!client.loyalty.next_tier && (
            <div className="bg-[#D4EDDA] border border-[#C3E6CB] rounded-lg p-3 flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-[#155724] flex-shrink-0" />
              <p className="text-xs text-[#155724]">Top tier reached! This client enjoys maximum benefits.</p>
            </div>
          )}

          {/* Recent loyalty activity */}
          {(client.loyalty.recent_history || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Recent Activity</p>
              <div className="space-y-1.5">
                {(client.loyalty.recent_history || []).slice(0, 5).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${h.type === "earn" ? "bg-[#D4EDDA] text-[#155724]" : "bg-[#FDE8E8] text-rose-600"}`}>
                      {h.type === "earn" ? "+" : "-"}
                    </span>
                    <span className="text-slate-600 flex-1">{h.reason}</span>
                    <span className={`font-semibold ${h.type === "earn" ? "text-emerald-600" : "text-rose-600"}`}>
                      {h.points > 0 ? "+" : ""}{h.points?.toLocaleString()} pts
                    </span>
                    <span className="text-rose-400">{h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="loyalty-not-enrolled" className="bg-white border border-dashed border-slate-200 rounded-2xl p-5 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-rose-400" />
            <div>
              <p className="text-sm font-medium text-slate-600">Not enrolled in Loyalty Program</p>
              <p className="text-xs text-slate-400">Enroll this client to start earning points</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              await api.post("/loyalty/enroll", { client_id: clientId });
              toast.success("Enrolled in loyalty program!");
              const res = await api.get(`/clients/${clientId}`);
              setClient(res.data);
            } catch (e) { toast.error(e.response?.data?.detail || "Failed to enroll"); }
          }} className="border-slate-200 text-slate-600" data-testid="enroll-loyalty-btn">
            <Plus className="w-4 h-4 mr-1" /> Enroll
          </Button>
        </div>
      )}

      {/* Client Score Card */}
      {score && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="client-score-card">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${score.tier_color}15` }}>
                <span className="font-['Outfit'] text-2xl font-bold" style={{ color: score.tier_color }}>{score.score}</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4" style={{ color: score.tier_color }} />
                  <span className="text-sm font-semibold px-2.5 py-0.5 rounded-full" style={{ backgroundColor: `${score.tier_color}15`, color: score.tier_color }}>
                    {score.tier}
                  </span>
                  {score.trend && score.trend !== "flat" && (
                    <span data-testid="score-trend" className={`flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded-full ${
                      score.trend === "up" ? "bg-[#E8F0E9] text-emerald-600" : "bg-rose-50 text-rose-600"
                    }`}>
                      <TrendingUp className={`w-3 h-3 ${score.trend === "down" ? "rotate-180" : ""}`} />
                      {score.score_delta > 0 ? "+" : ""}{score.score_delta || 0}
                    </span>
                  )}
                  {score.trend === "flat" && (
                    <span data-testid="score-trend" className="text-xs text-slate-400 px-2 py-0.5 rounded-full bg-[#FAFAFA]">Stable</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1 max-w-md">{score.explanation}</p>
                {score.updated_at && <p className="text-[10px] text-rose-400 mt-1">Last updated: {new Date(score.updated_at).toLocaleDateString()}</p>}
              </div>
            </div>
            <button onClick={async () => {
              const res = await api.post(`/client-scores/${clientId}/refresh`);
              setScore(res.data);
            }} className="text-xs text-slate-400 hover:text-slate-900 transition-colors">
              Refresh
            </button>
          </div>

          {/* Score Breakdown */}
          <div className="grid grid-cols-7 gap-2 mb-5">
            {score.breakdown && Object.entries(score.breakdown).map(([key, val]) => {
              const icons = { total_spend: DollarSign, rebooking: Calendar, product_purchases: ShoppingBag, reviews: MessageCircle, loyalty: Star, attendance: UserCheck, recency: Clock };
              const Icon = icons[key] || Zap;
              const s = val.score || 0;
              const barColor = s >= 60 ? '#2D5936' : s >= 30 ? '#805B10' : '#8A2A23';
              return (
                <div key={key} className="text-center">
                  <Icon className="w-4 h-4 mx-auto mb-1 text-slate-400" strokeWidth={1.5} />
                  <div className="w-full bg-[#FAFAFA] rounded-full h-1.5 mb-1">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${s}%`, backgroundColor: barColor }} />
                  </div>
                  <span className="text-[11px] font-semibold" style={{ color: barColor }}>{s}</span>
                  <p className="text-[9px] text-slate-400 mt-0.5 leading-tight capitalize">{key.replace(/_/g, ' ').replace('product purchases', 'Retail')}</p>
                </div>
              );
            })}
          </div>

          {/* Recommended Actions */}
          {score.actions && score.actions.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recommended Actions</p>
              <div className="flex flex-wrap gap-2">
                {score.actions.map((action, i) => (
                  <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                    action.priority === 'critical' ? 'bg-rose-50 text-rose-600' :
                    action.priority === 'high' ? 'bg-[#FDF3E1] text-amber-600' :
                    'bg-[#FAFAFA] text-slate-600'
                  }`} data-testid={`action-${action.type}`}>
                    {action.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="appointments" className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <TabsList className="w-full justify-start bg-[#FAFAFA] p-1 rounded-t-xl rounded-b-none border-b border-slate-200">
          <TabsTrigger data-testid="tab-appointments" value="appointments" className="text-sm">Appointments</TabsTrigger>
          <TabsTrigger data-testid="tab-formulas" value="formulas" className="text-sm">Color Formulas</TabsTrigger>
          <TabsTrigger data-testid="tab-transactions" value="transactions" className="text-sm">Transactions</TabsTrigger>
          <TabsTrigger data-testid="tab-cards" value="cards" className="text-sm">Cards on File</TabsTrigger>
          <TabsTrigger data-testid="tab-notes" value="notes" className="text-sm">Notes</TabsTrigger>
          <TabsTrigger data-testid="tab-activity" value="activity" className="text-sm">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="appointments" className="p-6">
          {appointments.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No appointments yet</p>
          ) : (
            <div className="space-y-3">
              {appointments.slice(0, 20).map((appt) => (
                <div key={appt.appointment_id} className="flex items-center justify-between py-3 border-b border-slate-200 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{appt.service_names?.join(", ")}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {appt.start_time ? new Date(appt.start_time).toLocaleString() : ""} with {appt.provider_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-900">${appt.total_price}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      appt.status === 'completed' ? 'bg-[#E8F0E9] text-emerald-600' :
                      appt.status === 'cancelled' ? 'bg-rose-50 text-rose-600' :
                      'bg-[#EAF0F6] text-blue-600'
                    }`}>
                      {appt.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Color Formulas Tab */}
        <TabsContent value="formulas" className="p-6" data-testid="formulas-tab">
          {/* Color Bar formulas — structured, with product cost & before/after photos */}
          <div className="mb-8" data-testid="colorbar-formulas-section">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-4 h-4 text-fuchsia-500" strokeWidth={1.5} />
              <h3 className="text-sm font-semibold text-slate-700 font-['Outfit']">Color Bar formulas</h3>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-fuchsia-600 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-2 py-0.5">cost &amp; photos</span>
            </div>
            <FormulaPanel clientId={clientId} clientName={client?.first_name ? `${client.first_name} ${client.last_name || ""}`.trim() : (client?.name || "")} />
          </div>

          <div className="border-t border-slate-200 my-6" />

          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-rose-400" strokeWidth={1.5} />
              <h3 className="text-sm font-semibold text-slate-700 font-['Outfit']">Quick formula notes</h3>
              <span className="text-xs text-slate-400 font-['Manrope']">{formulas.length} saved</span>
            </div>
            <Button size="sm" onClick={() => { setShowAddFormula(true); setEditingFormula(null); setFormulaForm({ title: "", formula: "", brand: "", developer: "", processing_time: "", notes: "" }); }}
              className="h-8 gradient-primary text-white rounded-lg px-3 text-xs font-semibold shadow-sm" data-testid="add-formula-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add note
            </Button>
          </div>

          {/* Add/Edit Formula Form */}
          {(showAddFormula || editingFormula) && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-5 space-y-3" data-testid="formula-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Title *</label>
                  <Input value={formulaForm.title} onChange={e => setFormulaForm(f => ({...f, title: e.target.value}))} placeholder="e.g., Full Balayage" className="h-9 border-slate-200 rounded-lg text-sm" data-testid="formula-title" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Brand</label>
                  <Input value={formulaForm.brand} onChange={e => setFormulaForm(f => ({...f, brand: e.target.value}))} placeholder="e.g., Wella, Redken, Schwarzkopf" className="h-9 border-slate-200 rounded-lg text-sm" data-testid="formula-brand" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Formula *</label>
                <textarea value={formulaForm.formula} onChange={e => setFormulaForm(f => ({...f, formula: e.target.value}))}
                  placeholder="e.g., Root: 6N + 7NW (1:1) &#10;Mids: 8N + 9G (2:1) &#10;Ends: 10V + Clear (1:1)"
                  className="w-full h-24 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 font-mono" data-testid="formula-formula" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Developer</label>
                  <Input value={formulaForm.developer} onChange={e => setFormulaForm(f => ({...f, developer: e.target.value}))} placeholder="e.g., 20 vol, 30 vol" className="h-9 border-slate-200 rounded-lg text-sm" data-testid="formula-developer" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Processing Time</label>
                  <Input value={formulaForm.processing_time} onChange={e => setFormulaForm(f => ({...f, processing_time: e.target.value}))} placeholder="e.g., 35 min" className="h-9 border-slate-200 rounded-lg text-sm" data-testid="formula-time" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Notes</label>
                <Input value={formulaForm.notes} onChange={e => setFormulaForm(f => ({...f, notes: e.target.value}))} placeholder="e.g., Client prefers cooler tones, sensitive scalp" className="h-9 border-slate-200 rounded-lg text-sm" data-testid="formula-notes" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" disabled={formulaSaving || !formulaForm.title.trim() || !formulaForm.formula.trim()} data-testid="save-formula-btn"
                  onClick={async () => {
                    setFormulaSaving(true);
                    try {
                      if (editingFormula) {
                        await api.put(`/clients/${clientId}/color-formulas/${editingFormula}`, formulaForm);
                        toast.success("Formula updated");
                      } else {
                        await api.post(`/clients/${clientId}/color-formulas`, formulaForm);
                        toast.success("Formula saved");
                      }
                      setShowAddFormula(false); setEditingFormula(null);
                      loadFormulas();
                    } catch { toast.error("Failed to save"); }
                    setFormulaSaving(false);
                  }}
                  className="h-8 bg-slate-900 text-white rounded-lg px-4 text-xs font-semibold">
                  <Save className="w-3 h-3 mr-1.5" />{formulaSaving ? "Saving..." : editingFormula ? "Update Formula" : "Save Formula"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowAddFormula(false); setEditingFormula(null); }} className="h-8 text-xs text-slate-500">Cancel</Button>
              </div>
            </div>
          )}

          {/* Formula Cards */}
          {formulas.length === 0 && !showAddFormula ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-3">
                <Palette className="w-6 h-6 text-rose-300" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-slate-400 font-['Manrope']">No color formulas saved yet</p>
              <p className="text-xs text-slate-300 mt-1 font-['Manrope']">Add this client's color recipes for easy reference</p>
            </div>
          ) : (
            <div className="space-y-3">
              {formulas.map(f => {
                const hasHistory = f.history && f.history.length > 0;
                const isHistoryOpen = expandedHistory === f.formula_id;
                return (
                <div key={f.formula_id} data-testid={`formula-${f.formula_id}`} className="bg-white border border-slate-200 rounded-xl p-4 group hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center">
                        <Pipette className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 font-['Manrope']">{f.title}</h4>
                        <p className="text-[10px] text-slate-400 font-['Manrope']">
                          {f.stylist_name} · {new Date(f.updated_at || f.created_at).toLocaleDateString()}
                          {f.brand && ` · ${f.brand}`}
                          {hasHistory && <span className="ml-1 text-violet-400">· v{(f.history?.length || 0) + 1}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {hasHistory && (
                        <button onClick={() => setExpandedHistory(isHistoryOpen ? null : f.formula_id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-violet-500 bg-violet-50 hover:bg-violet-100 transition-colors" data-testid={`history-toggle-${f.formula_id}`}>
                          <History className="w-3 h-3" strokeWidth={2} />
                          {f.history.length} prev
                          <ChevronDown className={`w-3 h-3 transition-transform ${isHistoryOpen ? "rotate-180" : ""}`} />
                        </button>
                      )}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditingFormula(f.formula_id); setShowAddFormula(false); setFormulaForm({ title: f.title, formula: f.formula, brand: f.brand || "", developer: f.developer || "", processing_time: f.processing_time || "", notes: f.notes || "" }); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors" data-testid={`edit-formula-${f.formula_id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={async () => { await api.delete(`/clients/${clientId}/color-formulas/${f.formula_id}`); toast.success("Formula deleted"); loadFormulas(); }}
                          className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors" data-testid={`delete-formula-${f.formula_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <pre className="text-sm text-slate-700 font-mono bg-slate-50 rounded-lg p-3 whitespace-pre-wrap leading-relaxed">{f.formula}</pre>
                  <div className="flex flex-wrap gap-3 mt-2.5">
                    {f.developer && (
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">Developer: {f.developer}</span>
                    )}
                    {f.processing_time && (
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">Time: {f.processing_time}</span>
                    )}
                  </div>
                  {f.notes && (
                    <p className="text-xs text-slate-400 mt-2 italic font-['Manrope']">{f.notes}</p>
                  )}

                  {/* Version History */}
                  {isHistoryOpen && hasHistory && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5" data-testid={`history-list-${f.formula_id}`}>
                      <p className="text-[10px] uppercase tracking-wider text-violet-400 font-bold">Version History</p>
                      {[...f.history].reverse().map((v, i) => (
                        <div key={i} className="bg-violet-50/40 border border-violet-100 rounded-lg p-3" data-testid={`history-v${v.version}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-bold text-violet-600">
                              v{v.version} — {v.title}
                            </span>
                            <span className="text-[9px] text-slate-400">
                              {v.updated_by} · {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : ""}
                            </span>
                          </div>
                          <pre className="text-xs text-slate-500 font-mono bg-white rounded-md p-2 whitespace-pre-wrap leading-relaxed">{v.formula}</pre>
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            {v.brand && <span className="text-[9px] text-slate-400">{v.brand}</span>}
                            {v.developer && <span className="text-[9px] text-slate-400">· Dev: {v.developer}</span>}
                            {v.processing_time && <span className="text-[9px] text-slate-400">· {v.processing_time}</span>}
                          </div>
                          {v.notes && <p className="text-[10px] text-slate-400 mt-1 italic">{v.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="transactions" className="p-6">
          {transactions.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No transactions yet</p>
          ) : (
            <div className="space-y-3">
              {transactions.slice(0, 20).map((txn) => (
                <div key={txn.transaction_id} className="flex items-center justify-between py-3 border-b border-slate-200 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {txn.items?.map(i => i.name).join(", ")}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : ""} via {txn.payment_method}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-slate-900">${txn.total?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cards" className="p-6" data-testid="cards-tab-content">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Saved Payment Methods</h3>
            <Button size="sm" onClick={() => setShowAddCard(true)} data-testid="add-card-btn"
              className="bg-slate-900 text-white rounded-full h-8 px-4 text-xs hover:bg-slate-800">
              <Plus className="w-3 h-3 mr-1" />Add Card
            </Button>
          </div>

          {/* Add Card Form */}
          {showAddCard && (
            <div className="border border-slate-200 rounded-2xl p-5 mb-4 bg-[#FAFAFA]" data-testid="add-card-form">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">New Card</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Cardholder Name</label>
                  <Input data-testid="card-holder-input" value={cardForm.cardholder_name} onChange={e => setCardForm(f => ({ ...f, cardholder_name: e.target.value }))}
                    placeholder="Name on card" className="border-slate-200 bg-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Card Number</label>
                  <Input data-testid="card-number-input" value={cardForm.card_number} onChange={e => setCardForm(f => ({ ...f, card_number: e.target.value.replace(/\D/g, "").slice(0, 16) }))}
                    placeholder="4242 4242 4242 4242" className="border-slate-200 bg-white font-mono tracking-wider" maxLength={16} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Exp Month</label>
                    <Input data-testid="card-exp-month" type="number" min={1} max={12} value={cardForm.exp_month} onChange={e => setCardForm(f => ({ ...f, exp_month: parseInt(e.target.value) || "" }))}
                      placeholder="MM" className="border-slate-200 bg-white" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Exp Year</label>
                    <Input data-testid="card-exp-year" type="number" min={2025} max={2040} value={cardForm.exp_year} onChange={e => setCardForm(f => ({ ...f, exp_year: parseInt(e.target.value) || "" }))}
                      placeholder="YYYY" className="border-slate-200 bg-white" />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={addCard} disabled={cardSaving} data-testid="save-card-btn"
                    className="bg-slate-900 text-white rounded-full px-5 h-9 text-xs hover:bg-slate-800">
                    {cardSaving ? "Saving..." : "Save Card"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowAddCard(false); setCardForm({ card_number: "", exp_month: "", exp_year: "", cardholder_name: "" }); }}
                    className="border-slate-200 rounded-full px-4 h-9 text-xs">Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* Card List */}
          {cards.length === 0 && !showAddCard ? (
            <div className="text-center py-10">
              <CreditCard className="w-10 h-10 text-[#E6E4E0] mx-auto mb-3" strokeWidth={1} />
              <p className="text-sm text-slate-400">No cards on file</p>
              <p className="text-xs text-rose-400 mt-1">Add a card to enable quick checkout</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cards.map(card => {
                const brandColors = { Visa: "#1A1F71", Mastercard: "#EB001B", Amex: "#006FCF", Discover: "#FF6000", Card: "#8A8A8A" };
                const color = brandColors[card.brand] || "#8A8A8A";
                return (
                  <div key={card.card_id} className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${card.is_default ? "border-[#2D5936]/30 bg-[#E8F0E9]/20" : "border-slate-200"}`}
                    data-testid={`card-${card.card_id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}12`, border: `1px solid ${color}30` }}>
                        <CreditCard className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{card.brand} ····{card.last4}</span>
                          {card.is_default && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8F0E9] text-emerald-600 font-medium">Default</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400">
                          {card.cardholder_name && `${card.cardholder_name} · `}Expires {card.exp_month}/{card.exp_year}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!card.is_default && (
                        <button onClick={() => setDefaultCard(card.card_id)} data-testid={`set-default-${card.card_id}`}
                          className="text-xs text-slate-400 hover:text-emerald-600 px-2 py-1 rounded-lg hover:bg-[#E8F0E9] transition-colors">
                          Set default
                        </button>
                      )}
                      <button onClick={() => deleteCard(card.card_id)} data-testid={`delete-card-${card.card_id}`}
                        className="w-7 h-7 rounded-full hover:bg-rose-50 flex items-center justify-center transition-colors">
                        <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" className="p-6">
          <div className="text-center py-8">
            <p className="text-sm text-slate-400">{client.notes || "No notes yet"}</p>
            {client.referral_source && (
              <p className="text-xs text-rose-400 mt-2">Referral source: {client.referral_source}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="p-6" data-testid="activity-tab-content">
          <ClientActivityLog clientId={clientId} refreshKey={activityRefresh} />
        </TabsContent>
      </Tabs>

      {/* Ban dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent data-testid="ban-dialog" className="sm:max-w-[460px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Ban className="w-5 h-5" /> Ban this client
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              They won't be able to book online until you unban. Existing appointments are kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason (optional)</label>
            <Input
              data-testid="ban-reason-input"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="e.g. Repeated no-shows, harassment"
              className="border-slate-200"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-ban-btn"
              onClick={submitBan}
              disabled={banSaving}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
            >
              {banSaving ? "Banning…" : "Ban client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog (manager PIN) */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => { setDeleteDialogOpen(o); if (!o) { setDeletePin(""); setDeleteError(""); } }}>
        <DialogContent data-testid="delete-dialog" className="sm:max-w-[460px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Delete client permanently
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This removes the profile, saved cards, color formulas, and loyalty balance. Past
              transactions stay on record. Future appointments are cancelled.
              <br /><span className="text-rose-600 font-medium">This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Manager PIN</label>
            <Input
              data-testid="delete-pin-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={deletePin}
              onChange={(e) => { setDeletePin(e.target.value); setDeleteError(""); }}
              placeholder="••••"
              className="border-slate-200 tracking-widest text-center font-mono"
              autoFocus
            />
            {deleteError && (
              <p data-testid="delete-pin-error" className="text-xs text-rose-600">{deleteError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-delete-btn"
              onClick={submitDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
            >
              {deleting ? "Deleting…" : "Delete client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
